
import { motion } from "framer-motion";
import {
  ExclamationTriangleIcon,
  AdjustmentsHorizontalIcon,
  BoltIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

export default function EtProblemStatement() {

  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose — Calm → Clarity → Sharp problems
  // Updated - 22 Nov 2025 - Cinematic Elevation — Clear, Urgent, Atmospheric
  // Updated - 22 Nov 2025 - Cinematic + Professional Icon Grid Version
  // Updated - 23 Nov 2025 - Copy Refinements

//   const problems = [
//     "Growth bottlenecks hidden beneath transactional data",
//     "Leaders overwhelmed by competing priorities",
//     "Lack of narrative clarity for teams and clients",
//     "Execution inconsistency rooted in habits, not strategy",
//   ];

    // const problems = [
    //     "Growth bottlenecks hidden beneath transactional data and dashboards.",
    //     "Leaders overwhelmed by noise, yet starved of clarity and focus.",
    //     "Organizations moving fast, but not always moving in the right direction.",
    //     "Teams lacking a shared narrative that aligns effort and energy.",
    //     "Execution inconsistency rooted not in strategy, but in habits and behaviors.",
    //     "Decisions influenced more by urgency than purpose."
    // ];
    
    // const problems = [
    //   {
    //     icon: AdjustmentsHorizontalIcon,
    //     text: "Growth bottlenecks hidden beneath transactional data.",
    //   },
    //   {
    //     icon: ExclamationTriangleIcon,
    //     text: "Leaders overwhelmed by noise, yet starved of clarity.",
    //   },
    //   {
    //     icon: ArrowPathIcon,
    //     text: "Organizations moving fast, but not always in the right direction.",
    //   },
    //   {
    //     icon: Cog6ToothIcon,
    //     text: "Teams lacking a shared narrative that aligns effort and energy.",
    //   },
    //   {
    //     icon: BoltIcon,
    //     text: "Execution inconsistency rooted in habits, not strategy.",
    //   },
    //   {
    //     icon: ClockIcon,
    //     text: "Decisions driven more by urgency than purpose.",
    //   },
    // ];

    // 23 Nov 2025
    const problems = [
      {
        icon: AdjustmentsHorizontalIcon,
        lines: [
          <>Growth bottlenecks sit beneath the surface.</>,
          <>Not in dashboards. But in the way leaders <span className="text-orange-600">think</span> and <span className="text-orange-600">decide</span>.</>,
        ],
      },
      {
        icon: ExclamationTriangleIcon,
        lines: [
          <>Leaders are surrounded by noise.</>,
          <>But starved of <span className="text-orange-600">clarity</span>.</>,
          <>And the space to make sense of what matters.</>,
        ],
      },
      {
        icon: ArrowPathIcon,
        lines: [
          <>Organizations move fast.</>,
          <>But not always in the <span className="text-orange-600">right direction</span>.</>,
        ],
      },
      {
        icon: Cog6ToothIcon,
        lines: [
          <>Teams work hard.</>,
          // <>But without a shared <span className="text-orange-600">narrative</span> that aligns effort and energy.</>,
          <>But without a shared <span className="text-orange-600">narrative</span> that communicates their <span className="text-orange-600">contribution</span> and energy.</>,
        ],
      },
      {
        icon: BoltIcon,
        lines: [
          // <>Execution breaks down.</>,
          <>Execution loses effectiveness.</>,
          // <>Not because of strategy. But because of <span className="text-orange-600">habits</span> and <span className="text-orange-600">behaviors</span>.</>,
          <>Not because of strategy. But from <span className="text-orange-600">habits</span> and <span className="text-orange-600">behaviors</span>.</>,
        ],
      },
      {
        icon: ClockIcon,
        lines: [
          <>Decisions are driven by urgency.</>,
          <>Not by <span className="text-orange-600">purpose</span>.</>,
        ],
      },
    ];

  return (

    <section className="relative overflow-hidden py-32 bg-black text-white">

      {/* Background gradient — warmer, no blue */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-orange-950/5 to-black"></div>

      {/* Soft bottom haze for warmth */}
      <div
        className="
          absolute bottom-0 left-1/2 -translate-x-1/2
          w-[900px] h-[400px]
          bg-orange-900/20 blur-[160px]
        "
      ></div>

      {/* Vertical center glow */}
      <div
        className="
          absolute left-1/2 top-0 -translate-x-1/2
          w-[500px] h-full
          bg-gradient-to-b from-orange-600/10 via-orange-500/5 to-transparent
          blur-[110px]
        "
      ></div>

      {/* Ember particles */}
      <div className="pointer-events-none absolute inset-0">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute w-[3px] h-[3px] bg-orange-400/25 rounded-full animate-problem-spark"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${6 + Math.random() * 4}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-6xl mx-auto px-6">

        {/* Heading */}
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-orange-500 mb-16 text-center"
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          The Real Bottlenecks Leaders Face
        </motion.h2>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-16">
          {problems.map((item, idx) => (
            <motion.div
              key={idx}
              className="flex flex-col items-start gap-5"
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: idx * 0.15 }}
            >
              {/* Icon */}
              <div
                className="
                  flex items-center justify-center 
                  h-14 w-14 rounded-full 
                  bg-gradient-to-br from-orange-600/20 to-black/20 
                  border border-orange-500/20 
                  shadow-[0_0_20px_rgba(255,125,0,0.25)]
                "
              >
                <item.icon className="h-8 w-8 text-orange-400 drop-shadow-[0_0_8px_rgba(255,125,0,0.5)]" />
              </div>

              {/* Lines */}
              <div className="text-gray-300 text-lg leading-relaxed font-light space-y-1">
                {item.lines.map((line, li) => (
                  <p key={li}>{line}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes problemSpark {
          0% { transform: translateY(0) scale(0.7); opacity: 0.2; }
          50% { transform: translateY(-30px) scale(1); opacity: 0.5; }
          100% { transform: translateY(0) scale(0.7); opacity: 0.2; }
        }
        .animate-problem-spark {
          animation: problemSpark linear infinite;
        }
      `}</style>

    </section>

    // <section className="relative overflow-hidden py-32 bg-black text-white">

    //   {/* Background gradient */}
    //   <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black"></div>

    //   {/* Glowing horizontal line */}
    //   <div className="absolute top-0 left-0 w-full flex justify-center">
    //     <div className="w-2/3 h-[2px] bg-gradient-to-r from-transparent via-orange-500/40 to-transparent"></div>
    //   </div>

    //   {/* Ambient particles */}
    //   <div className="pointer-events-none absolute inset-0">
    //     {[...Array(85)].map((_, i) => (
    //       <div
    //         key={i}
    //         className="absolute w-[3px] h-[3px] bg-orange-300/20 rounded-full animate-problem-embers"
    //         style={{
    //           top: `${Math.random() * 100}%`,
    //           left: `${Math.random() * 100}%`,
    //           animationDelay: `${Math.random() * 4}s`,
    //           animationDuration: `${6 + Math.random() * 5}s`,
    //         }}
    //       ></div>
    //     ))}
    //   </div>

    //   {/* Content */}
    //   <div className="relative z-20 max-w-6xl mx-auto px-6">
        
    //     {/* Heading */}
    //     <motion.h2
    //       className="text-3xl md:text-4xl font-bold text-orange-500 mb-16 text-center"
    //       initial={{ opacity: 0, y: 25 }}
    //       whileInView={{ opacity: 1, y: 0 }}
    //       viewport={{ once: true }}
    //       transition={{ duration: 1 }}
    //     >
    //       The Real Bottlenecks Leaders Face
    //     </motion.h2>

    //     {/* Icon + text grid */}
    //     <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-14">
    //       {problems.map((item, idx) => (
    //         <motion.div
    //           key={idx}
    //           className="flex flex-col items-start gap-4"
    //           initial={{ opacity: 0, y: 25 }}
    //           whileInView={{ opacity: 1, y: 0 }}
    //           viewport={{ once: true }}
    //           transition={{ duration: 1, delay: idx * 0.15 }}
    //         >
    //           {/* Icon */}
    //           <div
    //             className="
    //               flex items-center justify-center 
    //               h-12 w-12 rounded-full 
    //               bg-gradient-to-br from-orange-600/20 to-black/20 
    //               border border-orange-500/20 
    //               shadow-[0_0_15px_rgba(255,125,0,0.25)]
    //             "
    //           >
    //             <item.icon className="h-7 w-7 text-orange-400 drop-shadow-[0_0_6px_rgba(255,125,0,0.4)]" />
    //           </div>

    //           {/* Lines */}
    //           <div className="text-gray-300 text-md leading-relaxed font-extralight">
    //             {item.lines.map((line, li) => (
    //               <p key={li}>{line}</p>
    //             ))}
    //           </div>
    //         </motion.div>
    //       ))}
    //     </div>
    //   </div>

    //   {/* Animation keyframes */}
    //   <style>{`
    //     @keyframes problem-embers {
    //       0% { transform: translateY(0) scale(0.7); opacity: 0.2; }
    //       50% { transform: translateY(-25px) scale(1); opacity: 0.5; }
    //       100% { transform: translateY(0) scale(0.7); opacity: 0.2; }
    //     }
    //     .animate-problem-embers {
    //       animation: problem-embers linear infinite;
    //     }
    //   `}</style>
    // </section>

    // <section className="relative overflow-hidden py-32 bg-black text-white">

    //   {/* Background Gradient */}
    //   <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black"></div>

    //   {/* Glow Lines */}
    //   <div className="pointer-events-none absolute top-0 left-0 w-full flex justify-center">
    //     <div className="w-2/3 h-[2px] bg-gradient-to-r from-transparent via-orange-500/40 to-transparent"></div>
    //   </div>
    //   <div className="pointer-events-none absolute bottom-0 left-0 w-full flex justify-center">
    //     <div className="w-1/2 h-[2px] bg-gradient-to-r from-transparent via-orange-400/20 to-transparent"></div>
    //   </div>

    //   {/* Ambient Embers */}
    //   <div className="pointer-events-none absolute inset-0">
    //     {[...Array(200)].map((_, i) => (
    //       <div
    //         key={i}
    //         className="absolute w-[3px] h-[3px] bg-orange-300/25 rounded-full animate-problem-embers"
    //         style={{
    //           top: `${Math.random() * 100}%`,
    //           left: `${Math.random() * 100}%`,
    //           animationDelay: `${Math.random() * 4}s`,
    //           animationDuration: `${5 + Math.random() * 5}s`,
    //         }}
    //       ></div>
    //     ))}
    //   </div>

    //   {/* CONTENT */}
    //   <div className="relative z-20 max-w-6xl mx-auto px-6">

    //     {/* Heading */}
    //     <motion.h2
    //       className="text-3xl md:text-4xl font-bold text-orange-500 mb-16 text-center"
    //       initial={{ opacity: 0, y: 25 }}
    //       whileInView={{ opacity: 1, y: 0 }}
    //       viewport={{ once: true }}
    //       transition={{ duration: 1 }}
    //     >
    //       The Real Bottlenecks Leaders Face
    //     </motion.h2>

    //     {/* Icon Grid */}
    //     <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
    //       {problems.map((item, idx) => (
    //         <motion.div
    //           key={idx}
    //           className="flex items-start gap-4"
    //           initial={{ opacity: 0, y: 25 }}
    //           whileInView={{ opacity: 1, y: 0 }}
    //           viewport={{ once: true }}
    //           transition={{ duration: 1, delay: idx * 0.15 }}
    //         >
    //           {/* Icon with Trinity Glow */}
    //           <div
    //             className="
    //               flex items-center justify-center 
    //               h-12 w-12 rounded-full 
    //               bg-gradient-to-br from-orange-600/20 to-black/20 
    //               border border-orange-500/20 
    //               shadow-[0_0_15px_rgba(255,125,0,0.25)]
    //             "
    //           >
    //             <item.icon className="h-7 w-7 text-orange-400 drop-shadow-[0_0_8px_rgba(255,125,0,0.4)]" />
    //           </div>

    //           {/* Text */}
    //           <p className="text-gray-300 text-lg leading-relaxed font-extralight">
    //             {item.text}
    //           </p>
    //         </motion.div>
    //       ))}
    //     </div>
    //   </div>

    //   {/* Motion Keyframes */}
    //   <style>{`
    //     @keyframes problem-embers {
    //       0% { transform: translateY(0) scale(0.7); opacity: 0.2; }
    //       50% { transform: translateY(-25px) scale(1); opacity: 0.5; }
    //       100% { transform: translateY(0) scale(0.7); opacity: 0.2; }
    //     }
    //     .animate-problem-embers {
    //       animation: problem-embers linear infinite;
    //     }
    //   `}</style>
    // </section>
    // <section className="relative overflow-hidden py-28 bg-black text-white">

    //   {/* --- 1. Background Gradient Layer (Darker + Intense) --- */}
    //   <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black"></div>

    //   {/* --- 2. Top & Bottom Glow Lines (Sharper, Brighter) --- */}
    //   <div className="pointer-events-none absolute top-0 left-0 w-full flex justify-center">
    //     <div className="w-2/3 h-[2px] bg-gradient-to-r from-transparent via-orange-500/40 to-transparent"></div>
    //   </div>
    //   <div className="pointer-events-none absolute bottom-0 left-0 w-full flex justify-center">
    //     <div className="w-1/2 h-[2px] bg-gradient-to-r from-transparent via-orange-400/20 to-transparent"></div>
    //   </div>

    //   {/* --- 3. Particle Embers (sharper + fewer) --- */}
    //   <div className="pointer-events-none absolute inset-0">
    //     {[...Array(200)].map((_, i) => (
    //       <div
    //         key={i}
    //         className="absolute w-[2.5px] h-[2.5px] bg-orange-300/30 rounded-full animate-problem-ember"
    //         style={{
    //           top: `${Math.random() * 100}%`,
    //           left: `${Math.random() * 100}%`,
    //           animationDelay: `${Math.random() * 4}s`,
    //           animationDuration: `${4 + Math.random() * 4}s`,
    //         }}
    //       ></div>
    //     ))}
    //   </div>

    //   {/* --- 4. Content Block --- */}
    //   <div className="relative z-20 max-w-5xl mx-auto px-6">

    //     {/* Heading */}
    //     <motion.h2
    //       className="text-3xl md:text-4xl font-bold text-orange-600 mb-10 text-center md:text-left"
    //       initial={{ opacity: 0, y: 25 }}
    //       whileInView={{ opacity: 1, y: 0 }}
    //       viewport={{ once: true }}
    //       transition={{ duration: 1 }}
    //     >
    //       The Real Bottlenecks Leaders Face
    //     </motion.h2>

    //     {/* Problem Statements */}
    //     <motion.div
    //       className="space-y-6 text-gray-300 text-lg leading-relaxed font-extralight"
    //       initial="hidden"
    //       whileInView="visible"
    //       viewport={{ once: true }}
    //       variants={{
    //         hidden: {},
    //         visible: {
    //           transition: { staggerChildren: 0.2 }
    //         }
    //       }}
    //     >
    //       {problems.map((line, idx) => (
    //         <motion.div
    //           key={idx}
    //           className="flex items-start gap-3"
    //           variants={{
    //             hidden: { opacity: 0, x: -20 },
    //             visible: { opacity: 1, x: 0, transition: { duration: 0.8 } }
    //           }}
    //         >
    //           {/* Icon Dot */}
    //           <div className="mt-2 h-[10px] w-[10px] bg-orange-500 rounded-full shadow-[0_0_10px_rgba(255,125,0,0.5)]"></div>
    //           <p>{line}</p>
    //         </motion.div>
    //       ))}
    //     </motion.div>
    //   </div>

    //   {/* --- Motion Keyframes --- */}
    //   <style>{`
    //     @keyframes emberFloat {
    //       0% { transform: translateY(0) scale(0.8); opacity: 0.15; }
    //       50% { transform: translateY(-20px) scale(1); opacity: 0.5; }
    //       100% { transform: translateY(0) scale(0.8); opacity: 0.15; }
    //     }
    //     .animate-problem-ember {
    //       animation: emberFloat linear infinite;
    //     }
    //   `}</style>

    // </section>
  );
}